/**
 * hex 컬러값과 r, g, b 값을 받아서, 각각 더한 hex 컬러값을 반환합니다.
 * @param hex ex) "#3366ff" 또는 "3366ff"
 * @param r 더할 red 값 (-255 ~ 255)
 * @param g 더할 green 값 (-255 ~ 255)
 * @param b 더할 blue 값 (-255 ~ 255)
 * @returns "#rrggbb" 형태의 hex 컬러값
 */
export function addRGBToHex(hex: string, r: number, g: number, b: number): string {
  // # 제거
  hex = hex.replace(/^#/, '')
  // 3자리 hex는 6자리로 변환
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(x => x + x)
      .join('')
  }
  if (hex.length !== 6) throw new Error('올바른 hex 컬러값이 아닙니다.')

  // 각 색상 분리
  const numR = parseInt(hex.slice(0, 2), 16)
  const numG = parseInt(hex.slice(2, 4), 16)
  const numB = parseInt(hex.slice(4, 6), 16)

  // 더하고 0~255로 클램프
  const clamp = (v: number) => Math.max(0, Math.min(255, v))
  const newR = clamp(numR + r)
  const newG = clamp(numG + g)
  const newB = clamp(numB + b)

  // 다시 hex로 변환
  const toHex = (v: number) => v.toString(16).padStart(2, '0')
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`
}

/**
 * 무작위 hex 컬러값을 반환합니다. 예: "#a1b2c3"
 */
export function getRandomHexColor(): string {
  const r = Math.floor(Math.random() * 256)
  const g = Math.floor(Math.random() * 256)
  const b = Math.floor(Math.random() * 256)
  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  )
}

/**
 * 파일 용량을 kb, mb, gb 등의 단위로 변환합니다.
 * @param bytes 바이트 단위의 파일 크기
 * @param decimals 소수점 자릿수 (기본값: 2)
 * @returns 변환된 파일 크기 문자열 (예: "1.5 MB", "2.3 KB")
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export async function downloadFile(file_path: string, file_name?: string) {
  try {
    const api = `/api/download?url=${encodeURIComponent(file_path)}${
      file_name ? `&filename=${encodeURIComponent(file_name)}` : ''
    }`

    // 1) 서버에서 바이너리로 받아 강제 다운로드 (Safari 우회)
    const res = await fetch(api, { method: 'GET', cache: 'no-store', credentials: 'include' })
    if (!res.ok) throw new Error(`다운로드 실패 (${res.status})`)

    // 파일명: 쿼리 우선, 없으면 헤더에서 추출, 그래도 없으면 경로 기반
    let suggestedName = file_name || ''
    const cd = res.headers.get('Content-Disposition') || ''
    const matchFilename = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd)
    if (!suggestedName) {
      if (matchFilename) {
        suggestedName = decodeURIComponent(matchFilename[1] || matchFilename[2] || '')
      }
    }
    if (!suggestedName) {
      try {
        suggestedName = decodeURIComponent(file_path.split('/').pop() || 'download')
      } catch (_) {
        suggestedName = 'download'
      }
    }

    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = suggestedName
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Safari 일부 버전 호환 위해 약간 늦게 revoke
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)

    // // 파일 URL에서 파일명 추출
    // const fileName = file_name || file_path.split('/').pop() || 'download'

    // // 방법 1: 직접 링크로 다운로드 시도
    // const link = document.createElement('a')
    // link.href = file_path
    // link.download = fileName
    // link.target = '_blank'

    // // 다운로드 강제 설정
    // link.setAttribute('download', fileName)
    // link.style.display = 'none'

    // // 다운로드 실행
    // document.body.appendChild(link)
    // link.click()

    // // 정리
    // document.body.removeChild(link)
  } catch (error) {
    console.error('파일 다운로드 실패:', error)
    // 에러 처리 (toast 메시지 등)
  }
}

// HTML 내용을 정리하는 유틸리티 함수
export const cleanHtmlContent = (html: string): string => {
  if (!html) return ''

  // 간단하고 안전한 HTML 파싱
  let text = html
    // HTML 엔티티 디코딩
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    // 모든 HTML 태그 제거
    .replace(/<[^>]*>/g, '')
    .trim()

  // 연속된 공백 정리
  text = text.replace(/\s+/g, ' ')

  return text
}

export const redirectToLogin = () => {
  const currentUrl = window.location.href
  window.location.href = `/login?prev=${encodeURIComponent(currentUrl)}`
}
